import { getSystemLastUpdatedTimestamp } from "@/lib/system-last-updated";

const STORAGE_KEY = "ai-transform-system-last-updated-overrides";

interface SystemLastUpdatedOverrideLike {
  system_id: string;
  last_updated?: string;
  last_updated_time?: string;
}

type SystemLastUpdatedOverrides = Record<string, string>;

let inMemoryOverrides: SystemLastUpdatedOverrides | null = null;

function canUseStorage() {
  return typeof window !== "undefined";
}

function readOverrides(): SystemLastUpdatedOverrides {
  if (inMemoryOverrides) return inMemoryOverrides;
  if (!canUseStorage()) {
    inMemoryOverrides = {};
    return inMemoryOverrides;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      inMemoryOverrides = {};
      return inMemoryOverrides;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      inMemoryOverrides = {};
      return inMemoryOverrides;
    }

    inMemoryOverrides = Object.entries(parsed).reduce<SystemLastUpdatedOverrides>(
      (acc, [systemId, value]) => {
        if (typeof value === "string" && value.trim() !== "") {
          acc[systemId] = value;
        }
        return acc;
      },
      {},
    );

    return inMemoryOverrides;
  } catch {
    inMemoryOverrides = {};
    return inMemoryOverrides;
  }
}

function writeOverrides(overrides: SystemLastUpdatedOverrides) {
  inMemoryOverrides = overrides;
  if (!canUseStorage()) return;

  if (Object.keys(overrides).length === 0) {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

export function touchSystemLastUpdatedOverride(
  systemId: string,
  touchedAt = new Date().toISOString(),
): string {
  writeOverrides({
    ...readOverrides(),
    [systemId]: touchedAt,
  });

  return touchedAt;
}

export function clearSystemLastUpdatedOverride(systemId: string) {
  const overrides = readOverrides();
  if (!(systemId in overrides)) return;

  const rest = { ...overrides };
  delete rest[systemId];
  writeOverrides(rest);
}

export function applySystemLastUpdatedOverride<
  T extends SystemLastUpdatedOverrideLike,
>(system: T): T {
  const overrides = readOverrides();
  const override = overrides[system.system_id];

  if (!override) return system;

  const overrideTimestamp = new Date(override).getTime();
  if (!Number.isFinite(overrideTimestamp)) {
    clearSystemLastUpdatedOverride(system.system_id);
    return system;
  }

  const backendTimestamp = getSystemLastUpdatedTimestamp(system);
  if (backendTimestamp >= overrideTimestamp) {
    clearSystemLastUpdatedOverride(system.system_id);
    return system;
  }

  return {
    ...system,
    last_updated: override,
    last_updated_time: override,
  };
}

export function applySystemLastUpdatedOverrides<
  T extends SystemLastUpdatedOverrideLike,
>(systems: T[] | undefined): T[] | undefined {
  return systems?.map((system) => applySystemLastUpdatedOverride(system));
}
